import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { RefreshCcw, Clock, Truck } from "lucide-react";
import { PageHeader } from "../components/shared/PageHeader";
import { ErrorState } from "../components/shared/ErrorState";
import { Skeleton } from "../components/ui/skeleton";
import { Input } from "../components/ui/input";
import { LiveMapView } from "../components/routes/LiveMapView";
import { AddressEtaSearch } from "../components/routes/AddressEtaSearch";
import { apiClient, getApiErrorMessage } from "../lib/apiClient";
import { todayLocal } from "../lib/utils";
import { cn } from "../lib/utils";

const POLL_MS = 15000;

function timeAgo(epochSecondsStr) {
  const n = Number(epochSecondsStr);
  if (!n) return "never";
  const diff = Math.max(0, Math.floor(Date.now() / 1000) - n);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  return `${Math.floor(diff / 3600)}h ago`;
}

export default function LiveMap() {
  const [date, setDate] = useState(todayLocal());
  const [state, setState] = useState({ loading: true, error: null, data: null });
  const [focusedRouteId, setFocusedRouteId] = useState(null);
  const [searchedPoint, setSearchedPoint] = useState(null);
  const timerRef = useRef(null);

  const load = useCallback((d, silent = false) => {
    if (!silent) setState((s) => ({ ...s, loading: true, error: null }));
    apiClient
      .get("/routes/live-map", { params: { date: d } })
      .then(({ data }) => setState({ loading: false, error: null, data }))
      .catch((error) => setState({ loading: false, error: getApiErrorMessage(error, "Could not load live map."), data: null }));
  }, []);

  useEffect(() => {
    load(date);
    setFocusedRouteId(null);
    if (timerRef.current) clearInterval(timerRef.current);
    if (date === todayLocal()) {
      timerRef.current = setInterval(() => load(date, true), POLL_MS);
    }
    return () => clearInterval(timerRef.current);
  }, [date, load]);

  if (state.loading) {
    return (
      <div data-testid="live-map-page">
        <PageHeader title="Live Map" description="Every route for the day, per driver" />
        <Skeleton className="h-[500px] w-full" />
      </div>
    );
  }

  if (state.error || !state.data) {
    return (
      <div data-testid="live-map-page">
        <PageHeader title="Live Map" description="Every route for the day, per driver" />
        <ErrorState message={state.error || "No data."} onRetry={() => load(date)} />
      </div>
    );
  }

  const { data } = state;

  return (
    <div data-testid="live-map-page">
      <PageHeader
        title="Live Map"
        description={`${data.items.length} route${data.items.length === 1 ? "" : "s"} · ${data.date}`}
        actions={
          <div className="flex items-center gap-2">
            <Input type="date" className="w-40" value={date} onChange={(e) => setDate(e.target.value)} data-testid="live-map-date-input" />
            <Link
              to={`/deliveries?date=${date}`}
              className="inline-flex items-center gap-1.5 rounded-md border border-border bg-surface px-3 py-2 text-sm font-medium hover:bg-background"
              data-testid="live-map-deliveries-link"
            >
              <Truck size={16} strokeWidth={1.5} /> Deliveries
            </Link>
            <button type="button" onClick={() => load(date)} className="rounded-md border border-border bg-surface p-2 hover:bg-background" data-testid="live-map-refresh-button">
              <RefreshCcw size={16} strokeWidth={1.5} />
            </button>
          </div>
        }
      />

      <AddressEtaSearch onLocate={setSearchedPoint} />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[280px_1fr]">
        <div className="space-y-2" data-testid="live-map-driver-list">
          {data.items.length === 0 && (
            <div className="rounded-md border border-dashed border-border bg-surface py-10 text-center text-sm text-text-muted" data-testid="live-map-empty">
              No routes for this date.
            </div>
          )}
          {data.items.map((r) => (
            <button
              key={r.id}
              type="button"
              onClick={() => setFocusedRouteId(r.id)}
              className={cn(
                "flex w-full flex-col rounded-md border border-border bg-surface p-3 text-left transition-colors hover:bg-background",
                focusedRouteId === r.id && "ring-1 ring-primary"
              )}
              style={{ borderLeftColor: r.driver_color || "#9CA3AF", borderLeftWidth: 4 }}
              data-testid={`live-map-driver-card-${r.id}`}
            >
              <div className="flex items-center gap-2">
                <span className="h-2.5 w-2.5 flex-shrink-0 rounded-full" style={{ background: r.driver_color || "#9CA3AF" }} />
                <Link to={`/routes/${r.id}`} onClick={(e) => e.stopPropagation()} className="truncate text-sm font-medium text-text-main hover:underline" data-testid={`live-map-driver-link-${r.id}`}>
                  {r.driver_name || `Route #${r.id}`}
                </Link>
                {r.tracking_enabled && <span className="rounded-full bg-success/10 px-1.5 py-0.5 text-[10px] font-semibold text-success">LIVE</span>}
              </div>
              <p className="mt-1 text-xs text-text-muted">Route #{r.id} · {r.deliveries_done}/{r.deliveries_total} done</p>
              <p className="mt-1 flex items-center gap-1 text-[11px] text-text-muted">
                <Clock size={11} strokeWidth={1.5} />
                {r.tracking_enabled ? `Last ping ${timeAgo(r.live_location_at)}` : r.finished_at ? "Finished" : r.started_at ? "Tracking off" : "Not started"}
              </p>
            </button>
          ))}
        </div>

        <div className="h-[560px] overflow-hidden rounded-md border border-border" style={{ isolation: "isolate" }}>
          <LiveMapView outlet={data.outlet} items={data.items} focusedRouteId={focusedRouteId} searchedPoint={searchedPoint} />
        </div>
      </div>
    </div>
  );
}
